import React, { useCallback, useEffect, useState } from "react";
import {
  Download,
  ShieldCheck,
  LayoutDashboard,
  Briefcase,
  ArrowLeftRight,
  TrendingUp,
  Receipt,
  PieChart,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import TopBar from "../components/TopBar";
import TabNav from "../components/TabNav";
import OverviewSection from "../components/OverviewSection";
import HoldingsTable from "../components/HoldingsTable";
import PerformanceChart from "../components/PerformanceChart";
import BenchmarkStrip from "../components/BenchmarkStrip";
import FeesSection from "../components/FeesSection";
import DiversificationSection from "../components/DiversificationSection";
import { apiClient, API } from "../lib/api";
import { getToken } from "../lib/auth";
import { fmtInr, fmtPct, pnlClass } from "../lib/format";

function StatCard({ label, value, sub, valueClass = "", onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={`text-left bg-card p-6 border border-subtle group ${
        onClick ? "hover:border-lime/50 cursor-pointer transition-colors" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-muted-foreground uppercase-track">{label}</div>
        {onClick ? (
          <ChevronRight
            className="h-3.5 w-3.5 text-muted-foreground/60 group-hover:text-lime"
            strokeWidth={1.5}
          />
        ) : null}
      </div>
      <div className={`text-3xl font-semibold mt-2 tabular ${valueClass}`}>{value}</div>
      {sub ? <div className="text-xs text-muted-foreground mt-1.5">{sub}</div> : null}
    </Tag>
  );
}

export default function ClientPortal() {
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pr] = await Promise.all([
        apiClient.get("/client/portfolio"),
        apiClient.get("/client/performance"),
      ]);
      setData(p.data);
      setPerf(pr.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/client/report.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data?.client?.name || "portfolio"}_report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar subtitle="Client Portal" />
        <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }
  const t = data.totals;
  const c = data.client;
  const totalPnl = (t.unrealized_pnl || 0) + (data.realized_pnl || 0);

  const tabs = [
    { id: "overview", label: "Overview", icon: <LayoutDashboard className="h-3.5 w-3.5" strokeWidth={1.5} /> },
    { id: "holdings", label: "Holdings", icon: <Briefcase className="h-3.5 w-3.5" strokeWidth={1.5} />, count: data.holdings.length },
    { id: "diversification", label: "Diversification", icon: <PieChart className="h-3.5 w-3.5" strokeWidth={1.5} /> },
    { id: "transactions", label: "Transactions", icon: <ArrowLeftRight className="h-3.5 w-3.5" strokeWidth={1.5} />, count: data.transactions.length },
    { id: "performance", label: "Performance", icon: <TrendingUp className="h-3.5 w-3.5" strokeWidth={1.5} /> },
    { id: "fees", label: "Fees", icon: <Receipt className="h-3.5 w-3.5" strokeWidth={1.5} /> },
  ];

  return (
    <div className="min-h-screen bg-background">
      <TopBar subtitle="Client Portal" />
      <main className="px-4 sm:px-6 lg:px-10 py-5 sm:py-8">
        <div className="mb-8">
          <BenchmarkStrip />
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] text-muted-foreground uppercase-track flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" strokeWidth={1.5} /> Read-only portfolio
            </div>
            <h1 className="text-3xl font-semibold mt-1 tracking-tight">
              Welcome, {c.name.split(" ")[0]}
            </h1>
            <div className="text-sm text-muted-foreground mt-1">
              Tracking since <span className="font-mono">{c.start_date}</span>
            </div>
          </div>
          <button
            data-testid="client-download-pdf"
            onClick={downloadPdf}
            className="bg-lime hover:brightness-110 text-white px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] rounded-md flex items-center gap-2 self-start active:scale-[0.98]"
          >
            <Download className="h-4 w-4" strokeWidth={1.5} />
            Download statement
          </button>
        </div>

        <TabNav tabs={tabs} active={activeTab} onChange={setActiveTab} />

        <div className="mt-6">
          {activeTab === "overview" && (
            <div data-testid="tab-content-overview">
              <OverviewSection data={data} perf={perf} onJump={setActiveTab} />
            </div>
          )}

          {activeTab === "holdings" && (
            <section data-testid="tab-content-holdings">
              <HoldingsTable holdings={data.holdings} />
            </section>
          )}

          {activeTab === "diversification" && (
            <section data-testid="tab-content-diversification">
              <DiversificationSection scope="client" />
            </section>
          )}

          {activeTab === "transactions" && (
            <section data-testid="tab-content-transactions">
              <div className="border border-subtle bg-card overflow-x-auto">
                <table className="w-full text-sm" data-testid="client-tx-table">
                  <thead className="bg-secondary border-b border-subtle">
                    <tr className="text-[10px] uppercase-track text-muted-foreground">
                      <th className="text-left p-3 font-medium">Date</th>
                      <th className="text-left p-3 font-medium">Type</th>
                      <th className="text-left p-3 font-medium">Symbol</th>
                      <th className="text-right p-3 font-medium">Qty</th>
                      <th className="text-right p-3 font-medium">Price</th>
                      <th className="text-right p-3 font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-subtle hover:bg-secondary">
                        <td className="p-3 font-mono tabular text-xs">{tx.tx_date}</td>
                        <td className="p-3">
                          <span
                            className={`text-[10px] px-2 py-0.5 font-semibold uppercase-track ${
                              tx.tx_type === "BUY" ? "bg-profit-soft" : "bg-loss-soft"
                            }`}
                          >
                            {tx.tx_type}
                          </span>
                        </td>
                        <td className="p-3 font-mono">{tx.symbol}</td>
                        <td className="p-3 text-right tabular">{Number(tx.quantity).toLocaleString("en-IN")}</td>
                        <td className="p-3 text-right tabular">
                          ₹{Number(tx.price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                        <td className="p-3 text-right tabular">
                          ₹{(Number(tx.quantity) * Number(tx.price)).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    ))}
                    {data.transactions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-10 text-center text-sm text-muted-foreground">
                          No transactions yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "performance" && (
            <section className="space-y-5" data-testid="tab-content-performance">
              {perf?.summary ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
                  {[
                    ["Portfolio", perf.summary.portfolio_return],
                    ["NIFTY 50", perf.summary.nifty50_return],
                    ["SENSEX", perf.summary.sensex_return],
                    ["NIFTY 500", perf.summary.nifty500_return],
                  ].map(([label, val]) => (
                    <div key={label} className="bg-card p-4">
                      <div className="text-[10px] text-muted-foreground uppercase-track">{label}</div>
                      <div className={`text-xl font-semibold mt-1 tabular ${pnlClass(val)}`}>
                        {fmtPct(val)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
              <PerformanceChart series={perf?.series || []} />
              <div className="text-xs text-muted-foreground">Since {c.start_date}</div>
            </section>
          )}

          {activeTab === "fees" && (
            <section data-testid="tab-content-fees">
              <FeesSection scope="client" />
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
